import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { makeStyles, useTheme } from '@mui/styles';
import { useNavigate } from 'react-router-dom';

import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Divider from '@mui/material/Divider';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import AddIcon from '@mui/icons-material/Add';
import Chip from '@mui/material/Chip';

import SettingsApplicationsIcon from '@mui/icons-material/SettingsApplications';
import EqualizerIcon from '@mui/icons-material/Equalizer';
import AppsIcon from '@mui/icons-material/Apps';

import { get as _get } from 'lodash';
import Moment from 'moment';
import { scaleOrdinal as D3scaleOrdinal } from 'd3';

import { GroupsContext } from '../../../../data/groups-service';
import Validator, { useValidate, checkValidity } from '../../../../components/validator';
import DashboardService from '../../../../data/dashboard-service';
import DataContext from '../../../../data/data-context.js';

import PopupMenu from '../../../../components/popupmenu';

const useStyles = makeStyles(theme => ({
    root: {
    },
    label: {
        textTransform: 'capitalize',
        display: 'inline',
    },
    secondary: {
        backgroundColor: theme.palette.secondary.main,
    },
    primary: {
        backgroundColor: theme.palette.primary.main,
    },
    // editor classes
    editorRoot: {
        padding: theme.spacing(1),
    },
    editorFooter: {
        alignSelf: 'flex-end',
        width: '100%',
    },
    editorMargin: {
        margin: theme.spacing(2),
    },
    inlineChips: {
        display: 'inline-block',
        verticalAlign: 'middle',
        padding: 5,
        height: 30,
        paddingTop: 0,
    },
    inlineForm: {
        margin: theme.spacing(1),
        '& .MuiTextField-root': {
            margin: theme.spacing(1),
        },
    },
}));

const DEFAULTS = edit => edit
    ? { minW: 12, minH: 10 }
    : { minW: 4, minH: 7 };

// don't display until size-me can figure out what our size is
const MC2HealthAlertsWidget = (props) => {
    return !props.size || !props.size.width ? ''
        : (
            props.data.edit
                ? <Editor { ...props }/>
                : <Widget { ...props } />
        );
};
MC2HealthAlertsWidget.propTypes = {
    data: PropTypes.object.isRequired,
    size: PropTypes.object,
};

const Widget = (props) => {
    const classes = useStyles();
    const theme = useTheme();
    const navigate = useNavigate();
    const { groupKeys } = useContext(GroupsContext);

    const [, setErrors] = useState(0);
    const [data, setData] = useState([]);

    const fetch = controller => {
        props.setBusy(true);
        return DashboardService.mc2GetHealthAlerts({
            type: props.data.settings.type,
            target_sensors: _get(props.data.settings, 'targets.sensor', []).map(c => c.name),
            target_cmc: _get(props.data.settings, 'targets.cmc', []).map(c => c.name),
        }, controller).catch(err => {
            props.onError(err, props.data.title);
            setErrors(current => {
                props.setBusy(false, current > 2);
                if (current > 2) DashboardService.unsubscribe(props.data.id);
                return current + 1;
            });
            return 'error';
        });
    };

    const callback = res => {
        if (res === 'error' || res == null) return;
        setErrors(0);
        props.setBusy(false);
        setData(res || []);
    };

    const subscribe = () => {
        setErrors(0);
        DashboardService.subscribe(props.data.id, fetch, callback);
    };

    const label = s => (s || '').split('_').join(' ');

    const handleClickAlert = d => {
        if (!d) return;
        const page = d.type.toLowerCase() === 'sensor'
            ? `cmcs/sensors/health/${d.cmc}/${d.uuid}`
            : d.type.toLowerCase() === 'cmc'
                ? `cmcs/health/${d.uuid}`
                : 'sysem/mc2_health';
        navigate(`/${page}`);
    };

    const colors = D3scaleOrdinal()
        .range(theme.SeverityColors.concat(theme.ChartColors))
        .domain(groupKeys);

    // some of those dates coming back from the sensor are not in ISO format
    const sanitize = d => (d || '')
        .split('-')
        .map(s => s.length < 2 ? '0' + s : s)
        .join('-');

    // incoming
    useEffect(() => {
        subscribe();
        return () => {
            // unsubscribe to data feed while editing (widget control exit)
            // will also force a refresh when resubscribing after edit is done
            DashboardService.unsubscribe(props.data.id);
        };
    }, []);

    return (
        <div
            className={classes.root}
            style={{ width: props.size.width, height: props.size.height, overflow: 'auto' }}>
                 {data.length === 0 ? (
                            <Typography variant="body2" style={{ padding: 16, textAlign: 'center' }}>
                                No health alerts found
                            </Typography>
                        ) : ( data.map((d, i) => (
                <List key={`${d.uuid}_${i}`}>
                    <ListItem alignItems="flex-start">
                        <ListItemAvatar style={{ cursor: 'pointer' }} onClick={() => handleClickAlert(d)}>
                            <Avatar style={{ backgroundColor: colors(d.cmc) }}>
                                { d.type === 'sensor'
                                    ? <EqualizerIcon/>
                                    : d.type === 'cmc' ? <SettingsApplicationsIcon/>
                                        : <AppsIcon/>}
                            </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                            primary={
                                <React.Fragment>
                                    {d.type === 'sensor'
                                        ? `${d.cmc}: ${d.name}`
                                        : d.type === 'cmc' ? d.name
                                            : 'MC2'} &nbsp;
                                    <Typography
                                        component="span"
                                        variant="subtitle2"
                                        className={classes.label}
                                        color="secondary">
                                        {Moment(sanitize(d.since)).format(theme.date.long)}
                                    </Typography>
                                </React.Fragment>
                            }
                            secondary={
                                <React.Fragment>
                                    <Typography
                                        component="span"
                                        variant="body2"
                                        className={classes.label}
                                        color="textPrimary">
                                        {label(d.id)}: &nbsp;
                                    </Typography>
                                    {d.description}
                                </React.Fragment>
                            }
                        />
                    </ListItem>
                    {i < data.length - 1 && <Divider variant="inset" component="li" />}
                </List>
            )))}
        </div>
    );
};

Widget.propTypes = {
    data: PropTypes.object.isRequired,
    size: PropTypes.object,
    hover: PropTypes.bool,
    setBusy: PropTypes.func,
    onError: PropTypes.func,
};

const Editor = (props) => {
    const classes = useStyles();

    const { sensorNames } = useContext(DataContext);
    const { cmcs } = useContext(GroupsContext);

    const [title, setTitle] = useState(props.data.title);
    const [type, setType] = useState(props.data.settings.type || 'All');
    const [targets, setTargets] = useState(props.data.settings.targets || {
        sensor: [], cmc: []
    });
    const [sensors, setSensors] = useState([]);

    const handleTitleChange = (e, validID, valid) => {
        setTitle(e.target.value);
    };

    const handleTypeChange = e => setType(e.target.value);

    const handleRemoveCMC = t => {
        setTargets(current => {
            const i = (current.cmc || []).findIndex(tt => t.name === tt.name);
            if (i >= 0) current.cmc.splice(i, 1);
            // remove sensors not in the cmc list
            current.sensor = (current.sensor || [])
                .filter(s => !(current.cmc || []).length ||
                    current.cmc.find(c => c.name === s.cmc));
            return { ...current, cmc: [...current.cmc] }; // kick
        });
    };

    const handleAddCMC = t => {
        setTargets(current => {
            const i = (current.cmc || []).findIndex(tt => t.name === tt.name);
            if (i < 0) {
                current.cmc = [
                    ...(current.cmc || []),
                    { type: 'cmc', name: t.name },
                ];
                // remove sensors not in the cmc list
                current.sensor = (current.sensor || [])
                    .filter(s => current.cmc.find(c => c.name === s.cmc));
            }
            return { ...current };
        });
    };

    const handleRemoveSensor = t => {
        setTargets(current => {
            const i = (current.sensor || []).findIndex(tt => t.name === tt.name);
            if (i >= 0) current.sensor.splice(i, 1);
            return { ...current, sensor: [...current.sensor] }; // kick
        });
    };

    const handleAddSensor = t => {
        setTargets(current => {
            const i = (current.sensor || []).findIndex(tt => t.name === tt.name);
            if (i < 0) {
                current.sensor = [
                    ...(current.sensor || []),
                    { type: 'cmc', name: t.name, cmc: t.cmc },
                ];
            }
            return { ...current };
        });
    };

    const handleSaveChanges = () => {
        props.onOK({
            ...props.data,
            title,
            settings: {
                ...props.data.settings,
                type,
                targets,
            },
        });
    };

    useEffect(() => {
        // filter available sensor list
        setSensors(Object.entries(sensorNames)
            .map(([k, v]) => ({ name: v.name, uuid: k, cmc: v.cmc }))
            .filter(s => !(targets.cmc || []).length || targets.cmc.find(c => c.name === s.cmc))
        );
    }, [sensorNames, targets.cmc]);

    return (
        <Box
            display="flex" flexWrap="wrap"
            className={classes.editorRoot}
            style={{ width: props.size.width - 5, height: props.size.height - 20 }}>
            <Box>
                <div className={classes.inlineForm}>
                    <Validator
                        style={{ width: 200 }}
                        label="Title"
                        aria-label="Title"
                        value={title}
                        onChange={handleTitleChange}/>
                    <TextField
                        style={{ width: 100 }}
                        select
                        label="Alert Source"
                        aria-label="Alert Source"
                        value={type}
                        onChange={handleTypeChange}>
                        {['All', 'MC2', 'CMC', 'Sensor'].map((option) => (
                            <MenuItem key={option} value={option}>
                                {option}
                            </MenuItem>
                        ))}
                    </TextField>
                </div>
                {type !== 'MC2' && type !== 'All' &&
                    <div className={classes.editorMargin}>
                        <Grid container spacing={1} alignItems="flex-end">
                            <Grid item>
                                <PopupMenu
                                    onSelect={handleAddCMC}
                                    aria-label="Filter by cmc(s)"
                                    title="Filter by cmc(s)"
                                    items={cmcs}
                                    itemkey="uuid"
                                    itemTitle="name"
                                    button={
                                        <Button
                                            aria-label="filter"
                                            title="filter"
                                            variant="contained"
                                            color="primary"
                                            size="small"
                                            startIcon={<AddIcon/>}>CMC
                                        </Button>
                                    }/>
                                {(targets.cmc || []).map((t, i) =>
                                    <div key={`${t.name}_i`} className={classes.inlineChips}>
                                        <Chip
                                            color="default"
                                            label={t.name}
                                            onDelete={() => handleRemoveCMC(t)}
                                            className={classes.chip}/>
                                    </div>
                                )}
                            </Grid>
                        </Grid>
                    </div>
                }
                {type === 'Sensor' &&
                    <div className={classes.editorMargin}>
                        <Grid container spacing={1} alignItems="flex-end">
                            <Grid item>
                                <PopupMenu
                                    onSelect={handleAddSensor}
                                    aria-label="Filter by sensor(s)"
                                    title="Filter by sensor(s)"
                                    items={sensors}
                                    itemkey="uuid"
                                    itemTitle="name"
                                    button={
                                        <Button
                                            aria-label="filter"
                                            title="filter"
                                            variant="contained"
                                            color="primary"
                                            size="small"
                                            startIcon={<AddIcon/>}>Sensor
                                        </Button>
                                    }/>
                                {(targets.sensor || []).map((t, i) =>
                                    <div key={`${t.name}_i`} className={classes.inlineChips}>
                                        <Chip
                                            color="default"
                                            label={t.name}
                                            onDelete={() => handleRemoveSensor(t)}
                                            className={classes.chip}/>
                                    </div>
                                )}
                            </Grid>
                        </Grid>
                    </div>
                }
            </Box>
            <Box className={classes.editorFooter}>
                <DialogActions style={{ paddingBottom: 0 }}>
                    <Button
                        aria-label="ok"
                        onClick={handleSaveChanges}
                        color="primary">
                    OK
                    </Button>
                    <Button aria-label="cancel" onClick={props.onCancel} color="primary" autoFocus>
                    Cancel
                    </Button>
                </DialogActions>
            </Box>
        </Box>
    );
};
Editor.propTypes = {
    data: PropTypes.object.isRequired,
    size: PropTypes.object,
    onOK: PropTypes.func,
    onCancel: PropTypes.func,
};

export {
    MC2HealthAlertsWidget as default,
    DEFAULTS,
};
